import { useId, type ReactNode } from 'react';

import { Checkbox, itemVariants, Label } from '@mint-lib/tw-components';
import { cn } from '@mint-lib/tw-utils';

import type { GlossaryType } from '../../api/types.js';
import { GlossaryOptionLabel } from './GlossaryOptionLabel.jsx';

type Props = {
  glossaryType: GlossaryType;
  label: string;
  icon: ReactNode;
  readOnly?: boolean;
  selected?: boolean;
  disabled?: boolean;
  onSelect?: () => void;
};

export const GlossarySelectionListItem = ({
  glossaryType,
  label,
  icon,
  readOnly = false,
  selected = false,
  disabled = false,
  onSelect,
}: Props) => {
  const isSelectable = !readOnly && !disabled;
  const checkboxId = useId();
  const className = cn(
    'w-full appearance-none bg-transparent text-left font-[inherit]',
    itemVariants({
      selectionListItem: selected ? 'selected' : 'default',
    }),
    'm-0 min-h-8 cursor-pointer shadow-sm',
    !isSelectable && 'cursor-default shadow-none',
  );

  const content = (
    <GlossaryOptionLabel
      glossaryType={glossaryType}
      label={label}
      icon={icon}
      readOnly={readOnly}
    />
  );

  if (!isSelectable) {
    return (
      <div
        aria-disabled
        data-state={selected ? 'checked' : 'unchecked'}
        className={className}
      >
        {content}
      </div>
    );
  }

  return (
    <Label
      htmlFor={checkboxId}
      data-state={selected ? 'checked' : 'unchecked'}
      className={className}
    >
      <Checkbox
        id={checkboxId}
        checked={selected}
        onCheckedChange={onSelect}
        className="pointer-events-none"
      />
      {content}
    </Label>
  );
};
